// InstallBanner component
import { useState, useEffect } from "react";
import { X, Shield, Download, Share } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent } from "@/components/ui/dialog";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

const isStandalone = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    (navigator as any).standalone === true
  );
};

const InstallBanner = () => {
  const { user } = useAuth();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [showIOSOverlay, setShowIOSOverlay] = useState(false);
  const [showFallbackTip, setShowFallbackTip] = useState(false);
  const [isIOSDevice, setIsIOSDevice] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }

    if (sessionStorage.getItem("install-banner-dismissed")) {
      setDismissed(true);
    }

    if (isIOS()) {
      setIsIOSDevice(true);
    }

    // Android/Chrome: capture beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    const installedHandler = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);

    // Fallback: if beforeinstallprompt hasn't fired in 5s on non-iOS, show tip
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    if (!isIOS()) {
      fallbackTimer = setTimeout(() => {
        setShowFallbackTip(true);
      }, 5000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
      if (fallbackTimer) clearTimeout(fallbackTimer);
    };
  }, []);

  // Clear fallback when prompt is captured
  useEffect(() => {
    if (deferredPrompt) setShowFallbackTip(false);
  }, [deferredPrompt]);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setInstalled(true);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("install-banner-dismissed", "true");
  };

  // Only show to logged-in users who haven't installed and haven't dismissed
  if (!user || dismissed || installed) return null;

  // Need either a prompt, iOS, or fallback tip to show something
  const hasPrompt = !!deferredPrompt;
  const showBanner = hasPrompt || isIOSDevice || showFallbackTip;
  if (!showBanner) return null;

  return (
    <>
      <div
        className={cn(
          "fixed top-0 left-0 right-0 z-50 md:hidden",
          "animate-fade-up"
        )}
      >
        <div
          className={cn(
            "flex items-center gap-3 px-4 py-3",
            "bg-charcoal-dark/95 backdrop-blur-xl",
            "border-b border-cyan/20 shadow-[0_4px_20px_rgba(0,242,255,0.08)]"
          )}
        >
          <p className="flex-1 text-xs text-foreground leading-snug">
            📱 Access GreyRock AI faster!{" "}
            {isIOSDevice ? (
              <button onClick={() => setShowIOSOverlay(true)} className="text-cyan underline underline-offset-2 font-semibold">
                Tap Share → Add to Home Screen
              </button>
            ) : hasPrompt ? (
              <button onClick={handleInstall} className="text-cyan underline underline-offset-2 font-semibold">
                Install now
              </button>
            ) : (
              <span className="text-muted-foreground">Tap Share → Add to Home Screen to install.</span>
            )}
          </p>

          <button
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-charcoal-medium transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* iOS Install Overlay */}
      <Dialog open={showIOSOverlay} onOpenChange={setShowIOSOverlay}>
        <DialogContent className="bg-charcoal-dark border-glass-border max-w-sm text-center">
          <div className="space-y-6 py-2">
            <div className="flex justify-center">
              <div className="w-16 h-16 rounded-2xl bg-cyan/10 flex items-center justify-center">
                <Shield className="w-8 h-8 text-cyan" />
              </div>
            </div>

            <div>
              <h3 className="text-lg font-bold text-foreground mb-1">Install GreyRock AI</h3>
              <p className="text-sm text-muted-foreground">Follow these 3 simple steps:</p>
            </div>

            <div className="space-y-4 text-left">
              <div className="flex items-start gap-3 p-3 rounded-xl bg-charcoal-medium">
                <div className="w-8 h-8 rounded-full bg-cyan/20 flex items-center justify-center shrink-0 text-cyan font-bold text-sm">1</div>
                <div>
                  <p className="text-sm font-medium text-foreground">Tap the Share button</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    Look for the <Share className="w-3 h-3 inline text-cyan" /> icon at the bottom of Safari
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-charcoal-medium">
                <div className="w-8 h-8 rounded-full bg-cyan/20 flex items-center justify-center shrink-0 text-cyan font-bold text-sm">2</div>
                <div>
                  <p className="text-sm font-medium text-foreground">Scroll down in the menu</p>
                  <p className="text-xs text-muted-foreground">Find "Add to Home Screen" option</p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 rounded-xl bg-charcoal-medium">
                <div className="w-8 h-8 rounded-full bg-cyan/20 flex items-center justify-center shrink-0 text-cyan font-bold text-sm">3</div>
                <div>
                  <p className="text-sm font-medium text-foreground">Tap "Add to Home Screen"</p>
                  <p className="text-xs text-muted-foreground">Then tap "Add" to confirm</p>
                </div>
              </div>
            </div>

            <button
              onClick={() => setShowIOSOverlay(false)}
              className="w-full py-3 rounded-xl font-semibold bg-charcoal-medium border border-glass-border text-foreground hover:bg-charcoal-light transition-colors"
            >
              Got it
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InstallBanner;
